import { computed, ComputedRef, watch } from 'vue'

import { isIncidentDeleted } from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import {
  getSubparticipantProperties,
  SubParticipantType,
} from '@desktop/views/Relation/Sports/configuration'

import { useHistoryIncidentParticipantSettings } from './useHistoryIncidentParticipantSettings'
import { getSubParticipantIdValue, getSubParticipantNameValue } from './utils'

export type SubParticipantSettings = ReturnType<
  ReturnType<
    typeof useHistoryIncidentParticipantSettings
  >['getSubParticipantSettings']
>

export function useSubParticipantAvailability(
  incident: Incident,
  type: SubParticipantType,
  getSubParticipantSettings: (
    type: SubParticipantType,
  ) => SubParticipantSettings,
  eventIncidentsQueue: ReturnType<typeof useEventIncidentsQueue>,
): {
  settings: ComputedRef<SubParticipantSettings>
  showSelect: ComputedRef<boolean>
} {
  // computed
  const settings = computed(() => getSubParticipantSettings(type))

  const showSelect = computed(
    () => !!settings.value && settings.value.isAvailable(incident),
  )

  // methods
  function clearUnavailableSubParticipant(): void {
    const { propertyId, propertyName } = getSubparticipantProperties(type)
    const id = getSubParticipantIdValue(incident, propertyId)
    const name = getSubParticipantNameValue(incident, propertyName)

    if (id === null && name === null) {
      return
    }

    eventIncidentsQueue.updateIncident(
      {
        [propertyId]: null,
        [propertyName]: null,
      },
      incident,
    )
  }

  // init
  watch(
    () => ({
      settings: settings.value,
      available: showSelect.value,
    }),
    ({ settings: currentSettings, available }) => {
      // Deleted history rows stay mounted (read-only / disabled) but must not
      // enqueue mutations when `isAvailable` hides the select.
      if (currentSettings && !available && !isIncidentDeleted(incident)) {
        clearUnavailableSubParticipant()
      }
    },
    { immediate: true },
  )

  return {
    settings,
    showSelect,
  }
}
